// src/components/company/ProtectedPages.jsx
// Wrappers for pages that need permission checks

import React from "react";
import { usePermissions, PageGate, AccessDenied } from "../../contexts/PermissionContext";

// Import your existing page components
import CompanyTeamPage from "../../pages/company/CompanyTeamPage";
import OrgManagementPage from "../../pages/company/OrgManagementPage";
import CompanyFinanceIncomePage from "../../pages/company/CompanyFinanceIncomePage";
import CompanyConnectStripePage from "../../pages/company/ConnectCompanyStripePage";
import CompanySubscribePage from "../../pages/company/CompanySubscribePage";
import TransactionHistoryPage from "../../pages/company/TransactionHistoryPage";
import FraudAlerts from "../../components/FraudAlerts";
import FraudAlertsPage from "../../pages/company/Fraudalertspage";

// ============================================================================
// PROTECTED PAGE WRAPPERS
// ============================================================================

/**
 * Team Page - Owner/Admin only
 */
export function ProtectedTeamPage() {
  return (
    <PageGate 
      page="team_page" 
      fallback={<AccessDenied message="Only Owners and Admins can access the Team page." />}
    >
      <CompanyTeamPage />
    </PageGate>
  );
}

/**
 * Org Management Page - Owner/Admin only
 */
export function ProtectedOrgManagementPage() {
  return (
    <PageGate 
      page="org_management" 
      fallback={<AccessDenied message="Only Owners and Admins can access Organization Management." />}
    >
      <OrgManagementPage />
    </PageGate>
  );
}

/**
 * Income Statement Page - Owner/Admin only
 */
export function ProtectedIncomeStatementPage() {
  return (
    <PageGate 
      page="income_statement" 
      fallback={<AccessDenied message="Only Owners and Admins can view the Income Statement." />}
    >
      <CompanyFinanceIncomePage />
    </PageGate>
  );
}

/**
 * Stripe Settings Page - Owner/Admin only
 */
export function ProtectedStripePage() {
  return (
    <PageGate 
      page="stripe_settings" 
      fallback={<AccessDenied message="Only Owners and Admins can manage Stripe settings." />}
    >
      <CompanyConnectStripePage />
    </PageGate>
  );
}

/**
 * Billing/Subscriptions Page - Owner/Admin only
 */
export function ProtectedBillingPage() {
  return (
    <PageGate 
      page="subscriptions" 
      fallback={<AccessDenied message="Only Owners and Admins can manage billing." />}
    >
      <CompanySubscribePage />
    </PageGate>
  );
}


/**
 * Fraud Alerts Page - Owner/Admin only
 */
export function ProtectedFraudAlertsPage() {
  return (
    <PageGate 
      page="fraud_alerts" 
      fallback={<AccessDenied message="Only Owners and Admins can view fraud alerts." />}
    >
      <FraudAlertsPage />
    </PageGate>
  );
}

// ============================================================================
// DEPARTMENT-SCOPED DATA FILTER HOOK
// ============================================================================

/**
 * Hook to filter data based on user's department access.
 * 
 * Usage:
 *   const { filterByDepartment, canViewAll } = useDepartmentFilter();
 *   const visibleContracts = canViewAll ? contracts : filterByDepartment(contracts);
 */
export function useDepartmentFilter() {
  const { isOrgWide, departmentId } = usePermissions();
  
  const filterByDepartment = (items, deptKey = "department_id") => {
    if (isOrgWide) return items;
    if (!departmentId) return [];
    return items.filter(item => item[deptKey] === departmentId);
  };
  
  return {
    filterByDepartment,
    canViewAll: isOrgWide,
    userDepartmentId: departmentId,
  };
}

// ============================================================================
// APPROVAL BUTTON COMPONENT
// ============================================================================

/**
 * Approval button that only shows if user has approval permissions.
 * 
 * Usage:
 *   <ApprovalButton 
 *     type="timecard" 
 *     onApprove={() => handleApprove(id)}
 *   >
 *     Approve
 *   </ApprovalButton>
 */
export function ApprovalButton({ type, onApprove, children, className = "", disabled = false }) {
  const { hasPermission } = usePermissions();
  
  const permissionMap = {
    timecard: "approve_timecards",
    travel: "approve_travel",
    milestone: "approve_milestones",
  };
  
  const permission = permissionMap[type];
  
  if (!permission || !hasPermission(permission)) {
    return null;
  }
  
  return (
    <button
      onClick={onApprove}
      disabled={disabled}
      className={`bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
    </button>
  );
}